// trader_agent_frontend/src/hooks/useAICopilot.js
import { useState, useCallback, useEffect, useRef } from 'react';
import { aiCopilotService } from '../services/aiCopilotService';

export const useAICopilot = () => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [healthStatus, setHealthStatus] = useState(null);
  const [healthChecked, setHealthChecked] = useState(false);

  const isMounted = useRef(true);

  /**
   * Check AI service health
   */
  const checkHealth = useCallback(async () => {
    if (!isMounted.current) return { enabled: false, status: 'unmounted' };

    try {
      const health = await aiCopilotService.checkHealth();
      if (isMounted.current) {
        setHealthStatus(health);
        setHealthChecked(true);
      }
      return health;
    } catch (err) {
      console.error('Failed to check AI health:', err);
      if (isMounted.current) {
        setHealthStatus({ enabled: false, status: 'error' });
        setHealthChecked(true);
      }
      return { enabled: false, status: 'error' };
    }
  }, []);

  /**
   * Check health on component mount
   */
  useEffect(() => {
    let timeoutId;
    
    const checkHealthIfNeeded = async () => {
      if (!healthChecked && isMounted.current) {
        await checkHealth();
      }
    };
    
    // Debounce to prevent rapid consecutive checks
    timeoutId = setTimeout(checkHealthIfNeeded, 100);
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, [checkHealth, healthChecked]);

  const simpleQA = useCallback(async ({
    question,
    systemPrompt = "You are a helpful trading assistant.",
    correlationId = null
  }) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await aiCopilotService.simpleQA({
        question,
        systemPrompt,
        correlationId
      });
      
      const formatted = aiCopilotService.formatQAResponse(result);
      setAnalysis(formatted);
      return formatted;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Q&A failed';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Analyze a trading strategy
   */
  const analyzeStrategy = useCallback(async (strategyParams, marketContext = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await aiCopilotService.analyzeStrategy(strategyParams, marketContext);
      const formatted = aiCopilotService.formatAnalysis(result);
      setAnalysis(formatted);
      return formatted;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'AI analysis failed';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get usage statistics
   */
  const getUsageStats = useCallback(async () => {
    try {
      const stats = await aiCopilotService.getUsageStats();
      setUsageStats(stats);
      return stats;
    } catch (err) {
      console.error('Failed to get usage stats:', err);
      return null;
    }
  }, []);

  /**
   * Clear current analysis
   */
  const clearAnalysis = useCallback(() => {
    setAnalysis(null);
    setError(null);
  }, []);

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    setAnalysis(null);
    setLoading(false);
    setError(null);
    setUsageStats(null);
  }, []);

  /**
   * Enhanced strategy analysis with strategy type selection
   */
  const analyzeStrategyEnhanced = useCallback(async ({
    strategyType = 'momentum',
    strategyParams = {},
    marketContext = {},
    userQuery = null,
    correlationId = null
  }) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await aiCopilotService.analyzeStrategyEnhanced({
        strategyType,
        strategyParams,
        marketContext,
        userQuery,
        correlationId
      });
      const formatted = aiCopilotService.formatEnhancedAnalysis(result);
      setAnalysis(formatted);
      return formatted;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Enhanced AI analysis failed';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Free-form Q&A for trading and finance questions
   */
  const askQuestion = useCallback(async ({
    question,
    contextType = 'general',
    experienceLevel = 'intermediate',
    correlationId = null
  }) => {
    if (!isMounted.current) {
      throw new Error('Component unmounted');
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await aiCopilotService.askQuestion({
        question,
        contextType,
        experienceLevel,
        correlationId
      });
      
      if (isMounted.current) {
        const formatted = aiCopilotService.formatQAResponse(result);
        setAnalysis(formatted);
      }
      
      return result;
    } catch (err) {
      console.error('AI Q&A failed:', err);
      
      if (isMounted.current) {
        let errorMessage = 'AI Q&A failed';
        
        if (err.response) {
          errorMessage = err.response.data?.detail || err.response.statusText || errorMessage;
        } else if (err.request) {
          errorMessage = 'Network error - unable to reach AI service';
        } else {
          errorMessage = err.message || errorMessage;
        }
        
        setError(errorMessage);
      }
      
      throw err;
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, []);


  /**
   * Get detailed usage statistics for all endpoint types
   */
  const getDetailedUsageStats = useCallback(async () => {
    try {
      const stats = await aiCopilotService.getDetailedUsageStats();
      setUsageStats(stats);
      return stats;
    } catch (err) {
      console.error('Failed to get detailed usage stats:', err);
      return null;
    }
  }, []);

  return {
    // State
    analysis,
    loading,
    error,
    usageStats,
    healthStatus,
    
    // Actions
    analyzeStrategy,           // Legacy
    analyzeStrategyEnhanced,   // New enhanced
    askQuestion,              // New Q&A
    checkHealth,
    getUsageStats,            // Legacy usage
    getDetailedUsageStats,    // New detailed usage
    clearAnalysis,
    reset,
    
    // Helpers
    isEnabled: healthStatus?.enabled || false,
    hasAnalysis: !!analysis?.success,
    isCached: analysis?.cached || false,
    healthChecked,
    
    // New helpers
    simpleQA,
    isStrategyAnalysis: analysis?.queryType === 'strategy_analysis',
    isFreeFormQA: analysis?.queryType === 'free_form_qa',
    currentStrategyType: analysis?.strategyType,
    currentContextType: analysis?.contextType
  };
};